import type { DiagnosticEvent, DiagnosticEventInput } from './types';
import { initErrorTracker } from './errorTracker';
import { initNavigationTracker } from './navigationTracker';
import { initInteractionTracker } from './interactionTracker';

const MAX_BUFFER = 5000;

type Listener = (running: boolean) => void;

/**
 * Singleton service that captures diagnostic events into an in-memory
 * circular buffer. No network — export the buffer manually via
 * exportBuffer() or the Staging Panel UI.
 */
class DiagnosticService {
  private buffer: DiagnosticEvent[] = [];
  private _sessionId = crypto.randomUUID();
  private _running = false;
  private cleanups: (() => void)[] = [];
  private listeners = new Set<Listener>();

  get running() {
    return this._running;
  }

  get bufferSize() {
    return this.buffer.length;
  }

  /** Subscribe to start/stop changes. Returns unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.listeners.forEach((fn) => fn(this._running));
  }

  start() {
    if (this._running) return;
    this._running = true;
    this._sessionId = crypto.randomUUID();
    this.buffer = [];
    const send = (event: DiagnosticEventInput) => this.send(event);

    this.cleanups = [
      initErrorTracker(send),
      initNavigationTracker(send),
      initInteractionTracker(send),
    ];

    console.log(
      `%c🔴 Diagnostics recording (session ${this._sessionId.slice(0, 8)})`,
      'color: #e74c3c; font-weight: bold',
    );

    this.notify();
  }

  stop() {
    if (!this._running) return;
    this._running = false;
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];

    console.log(
      `%c⚪ Diagnostics stopped (${this.buffer.length} events captured)`,
      'color: #666; font-weight: bold',
    );

    this.notify();
  }

  send(event: DiagnosticEventInput) {
    if (!this._running) return;

    const full = {
      ...event,
      ts: Date.now(),
      sessionId: this._sessionId,
    } as DiagnosticEvent;

    this.buffer.push(full);
    if (this.buffer.length > MAX_BUFFER) {
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    }
  }

  exportBuffer(): string {
    return JSON.stringify(
      {
        sessionId: this._sessionId,
        userAgent: navigator.userAgent,
        url: location.href,
        exportedAt: new Date().toISOString(),
        eventCount: this.buffer.length,
        events: this.buffer,
      },
      null,
      2,
    );
  }

  downloadBuffer() {
    const blob = new Blob([this.exportBuffer()], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostics-${this._sessionId.slice(0, 8)}-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async copyBuffer(): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(this.exportBuffer());
      return true;
    } catch {
      return false;
    }
  }
}

export const diagnostics = new DiagnosticService();

if (typeof window !== 'undefined') {
  (window as any).__diagnostics = diagnostics;
}
